// components/TrackingBar.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fynitoApi } from "../Services/axios";
import { DEFAULT_APP_DOWNLOAD_URL } from "../../config/appDownload";
import { getOrderStatusCache, setOrderStatusCache } from "../../utils/orderStatusCache";

const TrackingBar = () => {
  const [orderId, setOrderId] = useState(null);
  const [status, setStatus] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [orderPlacedTime, setOrderPlacedTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  // Fetch latest order status: use shared cache first (set by OrderList) to avoid duplicate API calls
  const fetchLatestOrderStatusFromAPI = async (orderId) => {
    try {
      const numericId = orderId.replace(/^FYN0+/, '') || orderId.replace('FYN', '');
      if (!numericId || numericId === "00000000") return null;

      // Use shared cache (OrderList writes here when it polls) - skip API if fresh
      const cached = getOrderStatusCache(orderId, 20000);
      if (cached) return cached.status;

      const res = await fynitoApi.post("/order-detail", { id: numericId });
      if (res.data.status_code === 200 && res.data.data) {
        const status = res.data.data.status;
        setOrderStatusCache(orderId, status);
        return status;
      }
    } catch (error) {
      // Silently fail - use localStorage status as fallback
    }
    return null;
  };

  const fetchOrderStatus = async () => {
    setIsLoading(true);
    const storedOrderId = localStorage.getItem("latestOrderId");
    const storedStatus = localStorage.getItem("latestOrderStatus");
    const storedPaymentMethod = localStorage.getItem("latestOrderPaymentMethod");
    const storedOrderPlacedTime = localStorage.getItem("latestOrderPlacedTime");

    
    // Also log all localStorage items that start with 'latest'
    const allLatestItems = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('latest')) {
        allLatestItems[key] = localStorage.getItem(key);
      }
    }

    if (storedOrderId && storedStatus) {
      // Skip invalid/default order IDs - don't show tracking bar with invalid data
      if (storedOrderId === "FYN00000000" || !storedOrderId.trim()) {
        setShouldShow(false);
        setOrderId(null);
        setStatus(null);
        setPaymentMethod(null);
        setOrderPlacedTime(null);
        setIsLoading(false);
        return;
      }
      
      setOrderId(storedOrderId);
      setStatus(storedStatus);
      setPaymentMethod(storedPaymentMethod);
      setOrderPlacedTime(storedOrderPlacedTime);
      
      // Check if 2 hours have passed since order was placed
      let shouldHideDueToTime = false;
      if (storedOrderPlacedTime) {
        try {
          const orderTime = new Date(storedOrderPlacedTime);
          const currentTime = new Date();
          const timeDifference = currentTime - orderTime;
          const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
          
          if (timeDifference >= twoHoursInMs) {
            shouldHideDueToTime = true;
            // Clear localStorage when 2 hours have passed
            localStorage.removeItem("latestOrderId");
            localStorage.removeItem("latestOrderStatus");
            localStorage.removeItem("latestOrderPaymentMethod");
            localStorage.removeItem("latestOrderPlacedTime");
            setShouldShow(false);
            setOrderId(null);
            setStatus(null);
            setPaymentMethod(null);
            setOrderPlacedTime(null);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          // If date parsing fails, continue with normal flow
        }
      }
      
      // Verify status with API to ensure we have the latest status
      // This is important because status might have changed (e.g., to Rejected/Canceled)
      const apiStatus = await fetchLatestOrderStatusFromAPI(storedOrderId);
      const currentStatus = apiStatus || storedStatus; // Use API status if available, otherwise use stored
      
      // Update localStorage with latest status if API returned a different status
      if (apiStatus && apiStatus !== storedStatus) {
        const statusLower = apiStatus.toLowerCase().trim();
        const isDelivered = statusLower === "delivered";
        const isCanceled = statusLower === "canceled" || statusLower === "cancelled" || statusLower === "cancel";
        const isRejected = statusLower === "rejected" || statusLower === "reject";
        
        if (isDelivered || isCanceled || isRejected) {
          // Status changed to final state - clear localStorage immediately
          localStorage.removeItem("latestOrderId");
          localStorage.removeItem("latestOrderStatus");
          localStorage.removeItem("latestOrderPaymentMethod");
          localStorage.removeItem("latestOrderPlacedTime");
          setShouldShow(false);
          setOrderId(null);
          setStatus(null);
          setPaymentMethod(null);
          setOrderPlacedTime(null);
          setIsLoading(false);
          return;
        } else {
          // Status updated but still active - update localStorage
          localStorage.setItem("latestOrderStatus", apiStatus);
        }
      }
      
      // Only show tracking bar if:
      // 1. There is an order ID
      // 2. Status is not "Delivered" (case-insensitive)
      // 3. Status is not "Canceled" (case-insensitive)
      // 4. Status is not "Rejected" or "Reject" (case-insensitive)
      // 5. Not hidden due to time (2 hours passed)
      const statusLower = currentStatus.toLowerCase().trim();
      const isDelivered = statusLower === "delivered";
      const isCanceled = statusLower === "canceled" || statusLower === "cancelled" || statusLower === "cancel";
      const isRejected = statusLower === "rejected" || statusLower === "reject";
      
      // If order is delivered, canceled, or rejected, clear localStorage immediately
      if (isDelivered || isCanceled || isRejected) {
        localStorage.removeItem("latestOrderId");
        localStorage.removeItem("latestOrderStatus");
        localStorage.removeItem("latestOrderPaymentMethod");
        localStorage.removeItem("latestOrderPlacedTime");
        setShouldShow(false);
        setOrderId(null);
        setStatus(null);
        setPaymentMethod(null);
        setOrderPlacedTime(null);
        setIsLoading(false);
        return;
      }
      
      // Update state with current status (might be from API)
      setStatus(currentStatus);
      
      const shouldDisplay = !shouldHideDueToTime;
      setShouldShow(shouldDisplay);
      
    } else {
      // No order found, don't show tracking bar
      setShouldShow(false);
      setOrderId(null);
      setStatus(null);
      setPaymentMethod(null);
      setOrderPlacedTime(null);
    }
    
    // Simulate loading delay for better UX
    setTimeout(() => setIsLoading(false), 500);
  };

  useEffect(() => {
    // Initial load
    fetchOrderStatus();

    // Polling every 15 seconds (increased from 10 to reduce API calls)
    // Only poll if there's an active order
    let interval = null;
    const storedOrderId = localStorage.getItem("latestOrderId");
    const storedStatus = localStorage.getItem("latestOrderStatus");
    
    // Only start polling if order exists and is not in final state
    if (storedOrderId && storedStatus) {
      const statusLower = storedStatus.toLowerCase().trim();
      const isFinal = statusLower === "delivered" || 
                     statusLower === "canceled" || 
                     statusLower === "cancelled" || 
                     statusLower === "cancel" ||
                     statusLower === "rejected" || 
                     statusLower === "reject";
      
      if (!isFinal) {
        const pollIntervalMs = 15000;
        const runPoll = () => {
          if (document.visibilityState !== "visible") return;
          const currentStatus = localStorage.getItem("latestOrderStatus");
          if (currentStatus) {
            const currentStatusLower = currentStatus.toLowerCase().trim();
            const isCurrentlyFinal = currentStatusLower === "delivered" || 
                                     currentStatusLower === "canceled" || 
                                     currentStatusLower === "cancelled" || 
                                     currentStatusLower === "cancel" ||
                                     currentStatusLower === "rejected" || 
                                     currentStatusLower === "reject";
            if (!isCurrentlyFinal) fetchOrderStatus();
          }
        };
        interval = setInterval(runPoll, pollIntervalMs);
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchOrderStatus();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Set up one-time timeout to auto-hide exactly when 2 hours have passed
    let autoHideTimeout = null;
    const storedOrderPlacedTime = localStorage.getItem("latestOrderPlacedTime");
    if (storedOrderPlacedTime) {
      try {
        const orderTime = new Date(storedOrderPlacedTime);
        const currentTime = new Date();
        const timeDifference = currentTime - orderTime;
        const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
        
        if (timeDifference < twoHoursInMs) {
          // Calculate remaining time until 2 hours
          const remainingTime = twoHoursInMs - timeDifference;
          
          // Set timeout to clear localStorage exactly when 2 hours have passed
          autoHideTimeout = setTimeout(() => {
            localStorage.removeItem("latestOrderId");
            localStorage.removeItem("latestOrderStatus");
            localStorage.removeItem("latestOrderPaymentMethod");
            localStorage.removeItem("latestOrderPlacedTime");
            setShouldShow(false);
            setOrderId(null);
            setStatus(null);
            setPaymentMethod(null);
            setOrderPlacedTime(null);
          }, remainingTime);
        } else {
          // Already past 2 hours, clear immediately
          localStorage.removeItem("latestOrderId");
          localStorage.removeItem("latestOrderStatus");
          localStorage.removeItem("latestOrderPaymentMethod");
          localStorage.removeItem("latestOrderPlacedTime");
          setShouldShow(false);
          setOrderId(null);
          setStatus(null);
          setPaymentMethod(null);
          setOrderPlacedTime(null);
        }
      } catch (error) {
        // If date parsing fails, continue silently
      }
    }

    // Check for auto-hide every minute (60 seconds) as a backup
    const autoHideInterval = setInterval(() => {
      const storedOrderPlacedTime = localStorage.getItem("latestOrderPlacedTime");
      const storedOrderId = localStorage.getItem("latestOrderId");
      
      if (storedOrderPlacedTime && storedOrderId) {
        try {
          const orderTime = new Date(storedOrderPlacedTime);
          const currentTime = new Date();
          const timeDifference = currentTime - orderTime;
          const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
          
          if (timeDifference >= twoHoursInMs) {
            // 2 hours have passed - clear localStorage and hide tracking bar
            localStorage.removeItem("latestOrderId");
            localStorage.removeItem("latestOrderStatus");
            localStorage.removeItem("latestOrderPaymentMethod");
            localStorage.removeItem("latestOrderPlacedTime");
            setShouldShow(false);
            setOrderId(null);
            setStatus(null);
            setPaymentMethod(null);
            setOrderPlacedTime(null);
          }
        } catch (error) {
          // If date parsing fails, continue silently
        }
      }
    }, 60000); // Check every minute

    // Listen for localStorage changes from other components
    const handleStorageChange = (e) => {
      if (e.key === "latestOrderId" || e.key === "latestOrderStatus" || 
          e.key === "latestOrderPaymentMethod" || e.key === "latestOrderPlacedTime") {
        fetchOrderStatus();
      }
    };

    // Listen for custom events when localStorage is updated programmatically
    const handleOrderUpdate = () => {
      fetchOrderStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('orderStatusUpdated', handleOrderUpdate);

    return () => {
      if (interval) clearInterval(interval);
      clearInterval(autoHideInterval);
      if (autoHideTimeout) clearTimeout(autoHideTimeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('orderStatusUpdated', handleOrderUpdate);
    };
  }, []);

  // Don't render anything if we shouldn't show the tracking bar
  if (!shouldShow) {
    return null;
  }

  const getStatusIcon = (status) => {
    if (!status) return <i className="fas fa-utensils"></i>;
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('preparing') || statusLower.includes('cooking')) return <i className="fas fa-utensils"></i>;
    if (statusLower.includes('ready') || statusLower.includes('out')) return <i className="fas fa-truck"></i>;
    if (statusLower.includes('delivered')) return <i className="fas fa-check-circle"></i>;
    if (statusLower.includes('rejected') || statusLower.includes('canceled')) return <i className="fas fa-times-circle"></i>;
    return <i className="fas fa-clock"></i>;
  };

  const getPaymentMessage = (paymentMethod) => {
    if (!paymentMethod) return "You can pay online to avoid using cash";
    
    const paymentLower = paymentMethod.toLowerCase();
    
    // Check for cash/pay on delivery
    if (paymentLower.includes('cash') || paymentLower.includes('pay on delivery')) {
      return "You can pay online to avoid using cash";
    }
    
    // Check for online payments (razorpay, upi, phonepe, etc.)
    if (paymentLower.includes('razorpay') || paymentLower.includes('upi') || 
        paymentLower.includes('phonepe') || paymentLower.includes('online') ||
        paymentLower.includes('card') || paymentLower.includes('net banking')) {
      return "Payment completed online";
    }
    
    // Default message for other payment methods
    return "Payment method: " + paymentMethod;
  };

  const handleRefresh = () => {
    fetchOrderStatus();
  };

  return (
    <div className="modern-tracking-bar">
      <div className="tracking-bar-container">
        <div className={`tracking-bar-content ${status ? `status-${status.toLowerCase().replace(/\s+/g, '-')}` : ''} ${isLoading ? 'loading' : ''}`}>
          <div className="tracking-icon">
            {getStatusIcon(status)}
          </div>
          
          <div className="tracking-info">
            {orderId && status ? (
              <>
                <h6 className="tracking-title">
                  Food is being {status}
                </h6>
                <p className="tracking-subtitle">
                  Order #{orderId} • {getPaymentMessage(paymentMethod)}
                </p>
              </>
            ) : (
              <>
                <h6 className="tracking-title">
                  Track Your Orders
                </h6>
                <p className="tracking-subtitle">
                  Check the status of your recent orders
                </p>
              </>
            )}
          </div>

          <div className="tracking-actions">
            {orderId && status ? (
              <>
                {/* <Link to="/my-order" className="tracking-btn primary">View Order</Link> */}
                <a href={DEFAULT_APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" className="tracking-btn primary">
                  View Order
                </a>
              </>
            ) : (
              <>
                {/* <Link to="/my-order" className="tracking-btn secondary">My Orders</Link> */}
                <a href={DEFAULT_APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" className="tracking-btn secondary">
                  My Orders
                </a>
              </>
            )}
            
            {orderId && status && (
              <button 
                className="tracking-refresh-btn"
                onClick={handleRefresh}
                aria-label="Refresh order status"
                disabled={isLoading}
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingBar;
